import { ReactNode, useState } from 'react';
import AuthAPI from '../services/auth.service';
import TokenService from '../services/token.service';
import {
  IManager,
  IResetPasswordRequest,
  ISigninRequest
} from '../commons/interfaces/auth.interface';
import AuthContext from '../contexts/auth.context';

const authAPI = new AuthAPI();

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const initialUser = TokenService.getDataFromToken();
  const [user, setUser] = useState<IManager | null>(initialUser);
  const [loading, setLoading] = useState(false);

  const signin = async (requestData: ISigninRequest) => {
    try {
      setLoading(true);
      const userData = await authAPI.signin(requestData);
      setUser(userData);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const signout = async () => {
    await authAPI.signout();
    setUser(null);
  };

  const isLoggedIn = () => {
    const isRefreshTokenExpired = TokenService.isTokenExpired('REFRESH');

    return !isRefreshTokenExpired;
  };

  const isAccessTokenExpired = () => {
    const accessTokenExpired = TokenService.isTokenExpired('ACCESS');

    return !accessTokenExpired;
  };

  const refresh = async () => {
    const refreshToken = TokenService.getToken('REFRESH') as string;
    const userData = await authAPI.refreshTokenAuth(refreshToken);

    setUser(userData);
  };

  const recoveryPassword = async (email: string) => {
    try {
      setLoading(true);
      await authAPI.recoveryPassword(email);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const resetPassword = async (requestData: IResetPasswordRequest) => {
    try {
      setLoading(true);
      await authAPI.resetPassword(requestData);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const value = {
    user,
    signin,
    signout,
    isLoggedIn,
    refresh,
    loading,
    recoveryPassword,
    resetPassword,
    isAccessTokenExpired
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
